import React, {
    Fragment,
    useMemo,
    ReactElement,
    useState,
    type JSX,
} from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { Tag } from "@impero-com/ui";
import { Cascader } from "antd";
import { cx } from "class-variance-authority";
import Fuse, { FuseResult, FuseResultMatch } from "fuse.js";
import memoizeOne from "memoize-one";
import type {
    DefaultOptionType,
    SingleValueType,
} from "rc-cascader/lib/Cascader";

import type { Guid } from "utils/guid";

import { TagCategory } from "../types";

import { DropdownWithFooter } from "./dropdownWithFooter";
import { SelectedTags } from "./selectedTags";
import * as theme from "./theme.css";
import { BaseTag, EditableTagListPlusProps } from "./types";

interface CategoriesWithTags<Tag> {
    category: TagCategory;
    tags: Array<Tag>;
}
interface TagContext<Tag> {
    tagMap: Record<Guid, Tag>;
    categoriesWithTags: Array<CategoriesWithTags<Tag>>;
}

function getContext<Tag extends BaseTag>(
    allTags: Tag[],
    allTagCategories: Array<TagCategory>,
    selectedTagIds: Array<Guid>,
    isTagActive: (tag: Tag) => boolean,
    getgetTagCategoryId: (tag: Tag) => Guid
): TagContext<Tag> {
    const tagMap: Record<Guid, Tag> = {};
    const isTagActiveOrSelected = (tag: Tag): boolean =>
        isTagActive(tag) || selectedTagIds.includes(tag.id);

    allTags.forEach((tag) => {
        if (isTagActiveOrSelected(tag)) {
            tagMap[tag.id] = tag;
        }
    });

    const categoriesWithTags: CategoriesWithTags<Tag>[] = [];
    const stringCompare = Cmp.string();
    const sortedCategories = allTagCategories
        .slice()
        .toSorted(Cmp.map((cat) => cat.name, stringCompare));
    for (const category of sortedCategories) {
        let tags: Tag[] = [];

        // get tags for both tag types
        tags = allTags
            .filter((tag) => getgetTagCategoryId(tag) === category.id)
            .filter((tag) => isTagActiveOrSelected(tag))
            .toSorted(Cmp.map((tag: Tag) => tag.name, stringCompare));

        if (tags.length > 0) {
            categoriesWithTags.push({ category, tags });
        }
    }

    return { tagMap, categoriesWithTags };
}

export interface CascaderOption {
    label: string;
    value: string;
    children?: CascaderOption[];
}

export function EditableBaseTagListPlus<Tag extends BaseTag>({
    allTagCategories,
    selectedTagIds,
    readOnlyTagIds = [],
    setSelectedTagIds,
    allTags,
    isTagActive,
    getTagCategoryId,
    onCreateTag,
    cascaderClassName,
    onBlur,
    inputRef,
    canCreateTag = false,
    disabled = false,
    getParentContainer = (container) => container.parentNode as HTMLElement,
}: EditableTagListPlusProps<Tag>): JSX.Element | null {
    const context = useMemo(
        () =>
            getContext(
                allTags,
                allTagCategories,
                selectedTagIds,
                isTagActive,
                getTagCategoryId
            ),
        [
            allTagCategories,
            allTags,
            selectedTagIds,
            isTagActive,
            getTagCategoryId,
        ]
    );
    const options: CascaderOption[] = useMemo(
        () =>
            context.categoriesWithTags.map((categoryWithTags) => ({
                label: categoryWithTags.category.name,
                value: categoryWithTags.category.id,
                children: categoryWithTags.tags.map((tag) => ({
                    label: tag.name,
                    value: tag.id,
                })),
            })),
        [context.categoriesWithTags]
    );
    const fuse: Fuse<FuseSearchOption> = useMemo(
        () => buildFuse(context.categoriesWithTags),
        [context.categoriesWithTags]
    );

    const handleChange = (newValue: SingleValueType[]) => {
        if (disabled) {
            throw new Error(
                "handleChange should never be called if component is disabled"
            );
        }

        const tagIds: string[] = newValue
            .map((selectedItem) => {
                const [categoryId, categoryChildId] = selectedItem;
                if (
                    typeof categoryId === "number" ||
                    typeof categoryChildId === "number"
                ) {
                    throw new Error("Invalid id type");
                }
                const categorySelected = !categoryChildId;
                if (categorySelected) {
                    const categoryTags = context.categoriesWithTags.find(
                        (category) => category.category.id === categoryId
                    );
                    if (categoryTags === undefined) {
                        throw new Error(
                            `Expected categoriesWithTags to include categoryId: ${categoryId}`
                        );
                    }
                    return categoryTags.tags.map((tag) => tag.id);
                } else {
                    return [categoryChildId];
                }
            })
            .flat();
        setSelectedTagIds(tagIds);
    };

    const handleRemoveTag = (tagId: Guid) => {
        setSelectedTagIds(
            selectedTagIds.filter((currentTagId) => currentTagId !== tagId)
        );
    };

    const cascaderValue: [Guid, Guid][] = selectedTagIds.map((tagId) => {
        const category = context.categoriesWithTags.find((category) =>
            category.tags.some((tag) => tag.id === tagId)
        );
        if (category === undefined) {
            throw new Error(`Expected to find category for tagId: ${tagId}`);
        }
        return [category.category.id, tagId];
    });

    const dropdownRender = (menus: ReactElement) => {
        return (
            <DropdownWithFooter
                onCreateTag={onCreateTag}
                getTagCategoryId={getTagCategoryId}
                isTagActive={isTagActive}
                menus={menus}
                options={options}
                canCreateTag={canCreateTag}
                setSelectedTag={(v) =>
                    setSelectedTagIds([...selectedTagIds, v])
                }
                allTags={allTags}
            />
        );
    };

    const [cascaderOpen, setCascaderOpen] = useState(false);
    const cascaderClassNames = cx(
        cascaderClassName,
        theme.cascader,
        cascaderOpen && theme.cascaderOpen
    );

    const handleDropdownVisibleChange = (open: boolean) => {
        setCascaderOpen(open);
    };

    const cascaderSearchOpts = useMemo(() => {
        return {
            filter: (
                inputValue: string,
                [categoryOpt, tagOpt]: DefaultOptionType[]
            ): boolean => {
                const results = getFuseSearchResults(fuse, inputValue);

                // return true of results match either by tag or by category
                return results.some((result) => {
                    return (
                        (tagOpt.value === result.item.id &&
                            result.item.type === "TAG") ||
                        (categoryOpt.value === result.item.id &&
                            result.item.type === "CATEGORY")
                    );
                });
            },
            sort: (
                a: DefaultOptionType[],
                b: DefaultOptionType[],
                inputValue: string
            ) => {
                const results = getFuseSearchResults(fuse, inputValue);

                const getScore = ([
                    categoryOpt,
                    tagOpt,
                ]: DefaultOptionType[]): number => {
                    // ATTENTION: the smaller Fuse.JS score is, the better match it is.
                    // 0 (or very close to 0) means a perfect match.
                    const categoryScore =
                        results.find(
                            (result) =>
                                categoryOpt.value === result.item.id &&
                                result.item.type === "CATEGORY"
                        )?.score || 1;
                    const tagScore =
                        results.find(
                            (result) =>
                                tagOpt.value === result.item.id &&
                                result.item.type === "TAG"
                        )?.score || 1;
                    // Note: we want to slightly prioritize tag matches over category matches,
                    // so we decrease it's score.
                    return Math.min(categoryScore, tagScore * 0.5);
                };

                // Note: in case if the scores are the same, there is no need to compare results by name,
                // because values are already sorted by name and sort is stable.
                return getScore(a) - getScore(b);
            },
            render: (
                inputValue: string,
                [categoryOpt, tagOpt]: DefaultOptionType[],
                prefixCls: string
            ) => {
                // That's the way the original Antd's highlighter works
                // See: https://github.com/ant-design/ant-design/blob/4.24.16/components/cascader/index.tsx#L61-L63
                const highlighterClass = `${prefixCls}-menu-item-keyword`;

                // Safety: categoryOpt and tagOpt are actually of type CascaderOption,
                // meaning that `label` is always a string.
                const categoryName = categoryOpt.label as string;
                const tagName = tagOpt.label as string;

                const results = getFuseSearchResults(fuse, inputValue);
                const matchingTag = results.find(
                    (result) =>
                        result.item.id === tagOpt.value &&
                        result.item.type === "TAG"
                );
                const matchingCategory = results.find(
                    (result) =>
                        result.item.id === categoryOpt.value &&
                        result.item.type === "CATEGORY"
                );

                const highlightedCategoryNameParts = highlightMatches(
                    categoryName,
                    matchingCategory?.matches || [],
                    highlighterClass
                );
                const highlightedTagNameParts = highlightMatches(
                    tagName,
                    matchingTag?.matches || [],
                    highlighterClass
                );

                return [
                    ...highlightedCategoryNameParts,
                    " / ",
                    ...highlightedTagNameParts,
                ];
            },
        };
    }, [fuse]);

    return (
        <div className={theme.tagListPlusRoot}>
            <div className={theme.tagListPlusContainer}>
                {selectedTagIds.length === 0 && readOnlyTagIds.length === 0 ? (
                    <Tag dashed variant="applied">
                        {t("EditableTagListPlus_NewTag_NoTags")}
                    </Tag>
                ) : (
                    <SelectedTags
                        disabled={disabled}
                        allTags={allTags}
                        allTagCategories={allTagCategories}
                        selectedTagIds={selectedTagIds}
                        readOnlyTagIds={readOnlyTagIds}
                        getTagCategoryId={getTagCategoryId}
                        onRemoveTag={handleRemoveTag}
                    />
                )}
            </div>
            <Cascader
                multiple
                ref={inputRef}
                showSearch={cascaderSearchOpts}
                allowClear={false}
                disabled={disabled}
                className={cascaderClassNames}
                options={options}
                onBlur={onBlur}
                onChange={handleChange}
                value={cascaderValue}
                dropdownRender={dropdownRender}
                getPopupContainer={getParentContainer}
                // Hack - may break on antd update
                maxTagPlaceholder={
                    cascaderOpen ? (
                        <Fragment />
                    ) : (
                        t("EditableTagListPlus_NewTag_Placeholder")
                    )
                }
                maxTagCount={-1}
                tagRender={() => <Fragment />}
                open={cascaderOpen}
                onDropdownVisibleChange={handleDropdownVisibleChange}
                dropdownMatchSelectWidth
            />
        </div>
    );
}

// Split the given text into highlighted and non highlighted parts based on the given matches
// from Fuse.
function highlightMatches(
    text: string,
    matches: ReadonlyArray<FuseResultMatch> | undefined,
    highlightClassName: string
): React.ReactNode[] {
    // If by some reason there are no matches then just return the original text without any highlighting
    if (!matches || matches.length === 0) {
        return [text];
    }

    const highlightRangeTuples = matches[0].indices;
    const parts = [];
    let pos = 0;
    for (const [spanStart, spanEnd] of highlightRangeTuples) {
        // Non highlighted part
        parts.push(text.slice(pos, spanStart));

        // Highlighted part
        const highlightedPart = text.slice(spanStart, spanEnd + 1);
        parts.push(
            <span className={highlightClassName}>{highlightedPart}</span>
        );

        // Move the position to the start of the next non highlighted part
        pos = spanEnd + 1;
    }

    // Rest of non highlighted text if there is any remaining
    parts.push(text.slice(pos));

    return parts;
}

interface FuseSearchOption {
    type: "CATEGORY" | "TAG";
    id: Guid;
    name: string;
}

// fuse.search() is called multiple times with the same arguments from Cascader.showSearch.filter and Cascader.showSearch.sort callbacks,
// so we want to memorize the results.
const getFuseSearchResults = memoizeOne(
    (
        fuse: Fuse<FuseSearchOption>,
        inputValue: string
    ): FuseResult<FuseSearchOption>[] => {
        return fuse.search(inputValue);
    }
);

function buildFuse<Tag extends BaseTag>(
    categoriesWithTags: Array<{
        category: TagCategory;
        tags: Array<Tag>;
    }>
): Fuse<FuseSearchOption> {
    const searchOptions: FuseSearchOption[] = categoriesWithTags
        .map(({ category, tags }): FuseSearchOption[] => [
            { type: "CATEGORY", id: category.id, name: category.name },
            ...tags.map(
                (tag): FuseSearchOption => ({
                    type: "TAG",
                    id: tag.id,
                    name: tag.name,
                })
            ),
        ])
        .flat();

    const fuseOpts = {
        keys: ["name"],
        includeScore: true, // include the score in the result
        isCaseSensitive: false,
        shouldSort: false,
        includeMatches: true,
        findAllMatches: true,
        minMatchCharLength: 1,
        location: 0, // Determines approximately where in the text is the pattern expected to be found
        threshold: 0.3, // This controls how "fuzzy" the match should be (value between 0 and 1)
        distance: 100, // This determines how "close" the match must be to the search text
    };

    return new Fuse(searchOptions, fuseOpts);
}
